import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Link,
  Flex,
  Button,
  Center,
  Text,
  useColorModeValue,
  useTheme,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AleaFilterIcon from '../../icons/AleaFilterIcon.tsx';
import StatusBox from '../StatusBox/StatusBox';
import FeatureIcon from '../FeatureIcon/FeatureIcon.tsx';
import { StatusColors } from '../../constants/statusColors.ts';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';

interface FilterOption {
  label: string;
  value: string;
  id: string;
}

interface Filters {
  rstate?: FilterOption[];
  station_id?: FilterOption[];
  features?: FilterOption[];
  carehome_id?: FilterOption[];
  has_alea_set?: FilterOption[];
}

interface AleaListFilterProps {
  filters?: Filters;
  buttonSize?: string;
  buttonBg?: string;
  buttonHoverBg?: string;
  dropdownWidth?: string;
  isSimpleList?: boolean;
}

const AleaListFilter: React.FC<AleaListFilterProps> = ({ 
  filters,
  buttonSize = "50px",
  buttonBg = "white",
  buttonHoverBg = "#f8f8f8",
  dropdownWidth = "186px",
  isSimpleList = false,
}) => {
  // i18n translation hook
  const { t } = useTranslation();
  const theme = useTheme();

  // React Router hooks for navigating and getting the current location
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // URLSearchParams for handling URL parameters
  const params = new URLSearchParams(location.search);

  // State for temporary (uncommitted) filter selections
  const [tempSelectedItems, setTempSelectedItems] = useState<Record<string, string[]>>({
    rstate: [],
    station_id: [],
    features: [],
    carehome_id: [],
    has_alea_set: [],
  });
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [activeFilterItem, setActiveFilterItem] = useState<string | null>(null);

  // Theme colors
  const aleaColor = useColorModeValue('alea.500', 'alea.400');
  const aleaColorHover = useColorModeValue('alea.600', 'alea.300');
  const hoverBgColor = useColorModeValue('#F3F2F2', 'gray.700');
  
  // Get the actual color value from theme
  const aleaIconColor = theme.colors.alea[500];

  // Initialize filter selections from URL params
  useEffect(() => {
    // Initialize temp selections from URL params
    const initialTempItems: Record<string, string[]> = {
      rstate: [],
      station_id: [],
      features: [],
      carehome_id: [],
      has_alea_set: [],
    };

    params.forEach((value, key) => {
      if (['rstate', 'station_id', 'features', 'carehome_id', 'has_alea_set'].includes(key)) {
        const values = value.split(',');
        initialTempItems[key as keyof typeof initialTempItems] = values;
      }
    });
    
    setTempSelectedItems(initialTempItems);
  }, [location.search]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Only close the entire dropdown, not the active item
        setIsFilterDropdownOpen(false);
        // Keep active filter item open so it's still visible when dropdown reopens
        // setActiveFilterItem(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle filter icon click
  const handleFilterIconClick = () => {
    setIsFilterDropdownOpen((prev) => !prev);
    setActiveFilterItem(null);
  };

  // Handle filter item click
  const handleFilterItemClick = (item: string) => {
    setActiveFilterItem((prev) => (prev === item ? null : item));
  };

  /**
   * Update a query parameter in the URL
   *
   * @param {string} key - The query parameter key.
   * @param {string} value - The query parameter value.
   */
  const updateQueryParam = (key: string, value: string[] | string) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(','));
      } else {
        params.delete(key);
      }
    } else {
      const existingValues = params.get(key)?.split(',') || [];
      if (!existingValues.includes(value)) {
        existingValues.push(value);
      }
      params.set(key, existingValues.join(','));
    }
    
    // Reset to page 1 when filters change
    params.set('page', '1');
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  /**
   * Check if all station names are short (less than 4 characters)
   * and there are no more than 5 items to determine if we should use horizontal layout
   */
  const shouldUseHorizontalLayout = () => {
    const stationFilters = filters?.station_id || [];
      
    if (!stationFilters || stationFilters.length === 0) return false;
    
    // Check if there are no more than 5 items
    if (stationFilters.length > 5) return false;
    
    // Check if all items are short
    return stationFilters.every(station => station.label.length < 4);
  };

  // Calculate grid width based on station count
  const calculateGridWidth = () => {
    const stationFilters = filters?.station_id || [];
    return stationFilters.length * 50;
  };

  /**
   * Render filter options based on the provided filter key
   *
   * @param {keyof Filters} filterKey - The filter key.
   * @returns {JSX.Element[] | null} - The rendered filter options.
   */
  const renderFilterOptions = (filterKey: keyof Filters) => {
    if (!filters || !filters[filterKey]) return null;
    
    // Use horizontal layout for station_id if all stations have short names
    const useHorizontalLayout = filterKey === 'station_id' && shouldUseHorizontalLayout();

    // Get temporary selected items for this filter key
    const tempSelected = tempSelectedItems[filterKey] || [];

    // If using horizontal layout for living areas
    if (useHorizontalLayout) {
      // Get width based on number of stations
      const gridWidth = calculateGridWidth();
      const items = filters[filterKey]!;
      
      return (
        <Flex 
          flexWrap="wrap" 
          p="0" 
          gap="0" 
          width={`${gridWidth}px`}
          borderRadius="0"
        >
          {items.map((option, index) => {
            const isSelected = tempSelected.includes(option.id);
            const isFirst = index === 0;
            
            return (
              <Box
                key={option.id}
                width="50px"
                height="50px"
                cursor="pointer"
                onClick={() => handleTempFilterSelect(option.id, filterKey)}
                fontWeight="400"
                bg={isSelected ? aleaColor : "white"}
                color={isSelected ? "white" : "black"}
                border="1px solid #DCDCDC"
                borderTopLeftRadius={isFirst ? "20px" : "0"}
                borderTopRightRadius="0"
                borderBottomLeftRadius={isFirst ? "20px" : "0"}
                borderBottomRightRadius="0"
                px="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ 
                  bg: isSelected ? aleaColorHover : hoverBgColor    
                }}
                _active={{
                  bg: isSelected ? aleaColorHover : aleaColor,
                  color: "white"
                }}
                transition="background-color 0.2s, color 0.2s"
                m="0"
              >
                {option.label}
              </Box>
            );
          })}
        </Flex>
      );
    }

    // Standard vertical layout
    return (
      <Box>
        {filters[filterKey]!.map((option, index) => {
          const isSelected = tempSelected.includes(option.id);
          
          return (
            <Box
              key={option.id}
              width="100%"
              height="50px"
              cursor="pointer"
              onClick={() => handleTempFilterSelect(option.id, filterKey)}
              fontWeight="400"
              bg={isSelected ? aleaColor : "white"}
              color={isSelected ? "white" : "black"}
              borderBottom="1px solid #DCDCDC"
              borderTopLeftRadius={
                index === 0 ? '20px' : ''
              }
              borderBottomLeftRadius={
                index === filters[filterKey]!.length - 1 ? '20px' : '0'
              }
              borderBottomRightRadius={
                index === filters[filterKey]!.length - 1 ? '20px' : '0'
              }
              _hover={{ 
                bg: isSelected ? aleaColorHover : hoverBgColor    
              }}
              _active={{
                bg: isSelected ? aleaColorHover : aleaColor,
                color: "white"
              }}
              transition="background-color 0.2s, color 0.2s"
            >
              <Link
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                paddingLeft={filterKey === 'features' || filterKey === 'has_alea_set' ? "5px" : "15px"}
                h="50px"
                variant="outline"
                color={isSelected ? "white" : "black"}
                _hover={{
                  textDecoration: "none" 
                }}
                _active={{
                  color: "white"
                }}
                fontSize="18px"
                transition="color 0.2s"
              >
                {filterKey === 'rstate' && (
                  <StatusBox
                    status={option.value as keyof typeof StatusColors}
                    label={t(option.label)}
                    showText={true}
                    textColor={isSelected ? "white" : undefined}
                  />
                )}
                {filterKey === 'station_id' && option.label}
                {filterKey === 'carehome_id' && option.label}
                {filterKey === 'features' && (
                  <Flex alignItems="center" width="100%">
                    <Box 
                      width="50px" 
                      height="50px" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center" 
                      minWidth="50px"
                    >
                      <FeatureIcon
                        title={option.value}
                        active={true}
                        customColor={isSelected ? "white" : undefined}
                      />
                    </Box>
                    <Text fontSize="18px" ml="10px" color={isSelected ? "white" : "inherit"}>
                      {option.label}
                    </Text>
                  </Flex>
                )}
                {filterKey === 'has_alea_set' && (
                  <Flex alignItems="center" width="100%">
                    <Box 
                      width="50px" 
                      height="50px" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center" 
                      minWidth="50px"
                    >
                      {option.value === 'true' ? (
                        <AleaCheckIcon color={isSelected ? 'white' : aleaIconColor} />
                      ) : (
                        <Text fontSize="18px" color={isSelected ? 'white' : '#C6C6C6'}>––</Text>
                      )}
                    </Box>
                    <Text fontSize="18px" ml="10px" color={isSelected ? "white" : "inherit"}>
                      {option.label}
                    </Text>
                  </Flex>
                )}
              </Link>
            </Box>
          );
        })}
      </Box>
    );
  };
  
  // Handle temporary filter selection without applying immediately
  const handleTempFilterSelect = (itemId: string, category: keyof Filters) => {
    // Prevent event propagation to avoid closing the dropdown
    setTempSelectedItems(prev => {
      const currentSelected = [...(prev[category] || [])];
      const isSelected = currentSelected.includes(itemId);
      
      let newSelected;
      if (isSelected) {
        // Remove from temporary selection
        newSelected = currentSelected.filter(id => id !== itemId);
      } else {
        // For carehome_id and has_alea_set, only allow single selection
        if (category === 'carehome_id' || category === 'has_alea_set') {
          newSelected = [itemId];
        } else {
          // Add to temporary selection for other categories
          newSelected = [...currentSelected, itemId];
        }
      }
      
      // Apply filter immediately
      setTimeout(() => {
        updateQueryParam(category, newSelected);
      }, 100);
      
      return {
        ...prev,
        [category]: newSelected
      };
    });
  };

  // z-index values
  const backdropZIndex = 1500;
  const buttonZIndex = 1600;
  const dropdownZIndex = 1600;
  const subMenuZIndex = 1700;

  // Style for main dropdown items to provide proper feedback
  const getMainItemStyle = () => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    p: "15px",
    h: "50px",
    fontWeight: "400",
    fontSize: "18px",
    color: "black",
    _hover: { 
      bg: "#F3F2F2", 
      textDecoration: "none" 
    },
    borderBottom: "1px solid #DCDCDC",
    borderLeft: isSimpleList ? undefined : "1px solid #DCDCDC",
    transition: "background-color 0.2s"
  });

  // Handle simple list item click
  const handleSimpleListItemClick = (option: FilterOption) => {
    const params = new URLSearchParams(location.search);
    params.set('carehome_id', option.id);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setIsFilterDropdownOpen(false);
  };

  // Render simple list items
  const renderSimpleListItems = () => {
    if (!filters) return null;

    // Get the first available filter options
    const filterKey = Object.keys(filters)[0] as keyof Filters;
    const options = filters[filterKey];

    if (!options) return null;

    return options.map((option, index) => {
      const isSelected = params.get('carehome_id') === option.id;
      
      return (
        <Box
          key={option.id}
          width="100%"
          height="50px"
          cursor="pointer"
          onClick={() => handleSimpleListItemClick(option)}
          fontWeight="400"
          bg={isSelected ? aleaColor : "white"}
          color={isSelected ? "white" : "black"}
          borderBottom={index !== options.length - 1 ? "1px solid #DCDCDC" : undefined}
          borderTopLeftRadius={index === 0 ? '20px' : undefined}
          borderTopRightRadius={index === 0 ? '20px' : undefined}
          borderBottomLeftRadius={index === options.length - 1 ? '20px' : undefined}
          borderBottomRightRadius={index === options.length - 1 ? '20px' : undefined}
          _hover={{ 
            bg: isSelected ? aleaColorHover : hoverBgColor    
          }}
          _active={{
            bg: isSelected ? aleaColorHover : aleaColor,
            color: "white"
          }}
          transition="background-color 0.2s, color 0.2s"
        >
          <Link
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            paddingLeft="15px"
            h="50px"
            variant="outline"
            color={isSelected ? "white" : "black"}
            _hover={{
              textDecoration: "none" 
            }}
            _active={{
              color: "white"
            }}
            fontSize="18px"
            transition="color 0.2s"
            width="100%"
          >
            {option.label}
          </Link>
        </Box>
      );
    });
  };

  return (
    <Box ref={menuRef} position="relative">
      {/* Backdrop */}
      {isFilterDropdownOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.25)"
          zIndex={backdropZIndex}
          onClick={() => {
            setIsFilterDropdownOpen(false);
            setActiveFilterItem(null);
          }}
        />
      )}

      <Button
        onClick={handleFilterIconClick}
        height={buttonSize}
        width={buttonSize}
        borderRadius="20px"
        bg={buttonBg}
        _hover={{ bg: buttonHoverBg }}
        p={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        zIndex={isFilterDropdownOpen ? buttonZIndex : 'auto'}
      >
        <Center w="100%" h="100%">
          <AleaFilterIcon
            size={50}
            color={aleaIconColor}
          />
        </Center>
      </Button>

      {/* Filter Dropdown */}
      {isFilterDropdownOpen && (
        <Box
          position="absolute"
          top="calc(100% + 10px)"
          right="0"
          bg="white"
          borderRadius="20px"
          w={dropdownWidth}
          zIndex={dropdownZIndex}
          maxH={isSimpleList ? "300px" : undefined}
          overflowY={isSimpleList ? "auto" : undefined}
          borderTopLeftRadius={
            !isSimpleList && activeFilterItem === 'rstate' ? '0' : undefined
          }
          borderBottomLeftRadius={
            !isSimpleList && activeFilterItem === 'features' ? '0' : undefined
          }
          className="filter-dropdown"
          sx={!isSimpleList ? {
            // This CSS approach preserves border radius without overflow:hidden
            "& > div:first-of-type > a": {
              borderTopLeftRadius: activeFilterItem === 'rstate' ? '0' : '20px',
              borderTopRightRadius: "20px",
            },
            "& > div:last-child > a": {
              borderBottomLeftRadius: activeFilterItem === 'features' ? '0' : '20px',
              borderBottomRightRadius: "20px",
            }
          } : undefined}
        >
          {isSimpleList ? (
            // Simple list without child menus
            renderSimpleListItems()
          ) : (
            // Original filter with child menus
            <>
          {/* status */}
              <Box position="relative">
            <Link
              {...getMainItemStyle()}
              onClick={() => handleFilterItemClick('rstate')}
              borderTopLeftRadius={activeFilterItem === 'rstate' ? '0' : '20px'}
              borderTopRightRadius="20px"
            >
              {t('status')}
            </Link>
            {activeFilterItem === 'rstate' && (
              <Box
                display="flex"
                flexDirection="column"
                position="absolute"
                right="100%"
                top="0"
                bg="white"
                borderTopLeftRadius="20px"
                borderBottomLeftRadius="20px"
                borderBottomRightRadius="20px"
                zIndex={subMenuZIndex}
                width="250px"
              >
                {renderFilterOptions('rstate')}
              </Box>
            )}
          </Box>

          {/* care home */}
          {filters?.carehome_id && filters.carehome_id.length > 0 && (
            <Box position="relative">
              <Link
                {...getMainItemStyle()}
                onClick={() => handleFilterItemClick('carehome_id')}
              >
                {t('careHome')}
              </Link>
              {activeFilterItem === 'carehome_id' && (
                <Box
                  display="flex"
                  flexDirection="column"
                  position="absolute"
                  right="100%"
                  top="0"
                  bg="white"
                  borderRadius="20px"
                  zIndex={subMenuZIndex}
                  borderTopRightRadius={0}
                  width="250px"
                >
                  {renderFilterOptions('carehome_id')}
                </Box>
              )}
            </Box>
          )}

          {/* living area */}
              <Box position="relative">
            <Link
              {...getMainItemStyle()}
              onClick={() => handleFilterItemClick('station_id')}
            >
              {t('livingArea')}
            </Link>
            {activeFilterItem === 'station_id' && (
              <Box
                display="flex"
                flexDirection="column"
                position="absolute"
                right="100%"
                top="0"
                bg="white"
                borderRadius="20px"
                zIndex={subMenuZIndex}
                borderTopRightRadius={0}
                width={shouldUseHorizontalLayout() ? `${calculateGridWidth()}px` : "250px"}
              >
                {renderFilterOptions('station_id')}
              </Box>
            )}
          </Box>

          {/* alea set */}
          <Box position="relative">
            <Link
              {...getMainItemStyle()}
              onClick={() => handleFilterItemClick('has_alea_set')}
            >
              {t('aleaSet')}
            </Link>
            {activeFilterItem === 'has_alea_set' && (
              <Box
                display="flex"
                flexDirection="column"
                position="absolute"
                right="100%"
                top="0"
                bg="white"
                borderRadius="20px"
                zIndex={subMenuZIndex}
                borderTopRightRadius={0}
                width="250px"
              >
                {renderFilterOptions('has_alea_set')}
              </Box>
            )}
          </Box>

          {/* active functions */}
          <Box position="relative">
            <Link
              {...getMainItemStyle()}
              onClick={() => handleFilterItemClick('features')}
              borderBottomLeftRadius={activeFilterItem === 'features' ? '0' : '20px'}
              borderBottomRightRadius="20px"
            >
              {t('features')}
            </Link>
            {activeFilterItem === 'features' && (
              <Box
                display="flex"
                flexDirection="column"
                position="absolute"
                right="100%"
                top="0"
                bg="white"
                borderRadius="20px"
                borderTopRightRadius={0}
                zIndex={subMenuZIndex}
                width="400px"
              >
                {renderFilterOptions('features')}
              </Box>
            )}
          </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AleaListFilter; 